import { useState, useEffect } from 'react';
import { DollarSign, MapPin, Clock, X, CheckSquare, Square, Tag, Check, AlertCircle, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { fetchCounties, groupCountiesByProvince, County } from '@/services/locationService';
import { fetchCategories, Category } from '@/services/categoryService';

interface Props {
  data: any;
  onChange: (data: any) => void;
}

export function ServicesPricingStep({ data, onChange }: Props) {
  const [counties, setCounties] = useState<County[]>([]);
  const [countiesByProvince, setCountiesByProvince] = useState<Record<string, County[]>>({});
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [selectedCounties, setSelectedCounties] = useState<string[]>(data.serviceCounties || []);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(data.specializations || []);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    loadCounties();
    loadCategories();
  }, []);

  const loadCounties = async () => {
    try {
      setLoading(true);
      const fetchedCounties = await fetchCounties();
      setCounties(fetchedCounties);
      setCountiesByProvince(groupCountiesByProvince(fetchedCounties));
    } catch (error) {
      console.error('Failed to load counties:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      setCategoriesLoading(true);
      const fetchedCategories = await fetchCategories();
      setCategories(fetchedCategories);
    } catch (error) {
      console.error('Failed to load categories:', error);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const toggleCounty = (countyId: string) => {
    const newSelected = selectedCounties.includes(countyId)
      ? selectedCounties.filter(id => id !== countyId)
      : [...selectedCounties, countyId];
    
    setSelectedCounties(newSelected);
    onChange({ ...data, serviceCounties: newSelected });
  };

  const toggleProvince = (province: string) => {
    const provinceCountyIds = countiesByProvince[province]?.map(c => c.id) || [];
    const allSelected = provinceCountyIds.every(id => selectedCounties.includes(id));
    
    let newSelected: string[];
    if (allSelected) {
      newSelected = selectedCounties.filter(id => !provinceCountyIds.includes(id));
    } else {
      newSelected = [...new Set([...selectedCounties, ...provinceCountyIds])];
    }
    
    setSelectedCounties(newSelected);
    onChange({ ...data, serviceCounties: newSelected });
  };

  const isProvinceFullySelected = (province: string): boolean => {
    const provinceCountyIds = countiesByProvince[province]?.map(c => c.id) || [];
    return provinceCountyIds.length > 0 && provinceCountyIds.every(id => selectedCounties.includes(id));
  };

  const isProvincePartiallySelected = (province: string): boolean => {
    const provinceCountyIds = countiesByProvince[province]?.map(c => c.id) || [];
    return provinceCountyIds.some(id => selectedCounties.includes(id)) && !isProvinceFullySelected(province);
  };

  const toggleCategory = (categoryId: string) => {
    const newSelected = selectedCategories.includes(categoryId)
      ? selectedCategories.filter(id => id !== categoryId)
      : [...selectedCategories, categoryId];
    
    setSelectedCategories(newSelected);
    onChange({ ...data, specializations: newSelected });
    if (showErrors && newSelected.length > 0) setShowErrors(false);
  };

  const selectAllCounties = () => {
    const allCountyIds = counties.map(c => c.id);
    setSelectedCounties(allCountyIds);
    onChange({ ...data, serviceCounties: allCountyIds });
  };

  const clearAllCounties = () => {
    setSelectedCounties([]);
    onChange({ ...data, serviceCounties: [] });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center pb-4 border-b border-gray-200">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Services & Pricing</h2>
        <p className="text-gray-600">Define what you offer and where you serve</p>
      </div>

      {/* Step 1: Service Categories */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
              <Tag className="w-4 h-4 text-primary-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Service Categories</h3>
              <p className="text-xs text-gray-500">Select all that apply</p>
            </div>
          </div>
          {selectedCategories.length > 0 && (
            <Badge variant="success" size="sm">
              {selectedCategories.length} selected
            </Badge>
          )}
        </div>

        {categoriesLoading ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-3 text-sm text-gray-500">Loading categories...</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {categories.map((category) => {
                const isSelected = selectedCategories.includes(category.id);
                return (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => toggleCategory(category.id)}
                    className={`relative p-4 rounded-xl border-2 transition-all text-left group ${
                      isSelected
                        ? 'border-primary-500 bg-primary-50 shadow-sm'
                        : 'border-gray-200 bg-white hover:border-primary-300 hover:shadow-sm'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-gray-900 mb-1">{category.name}</div>
                        {category.job_count > 0 && (
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <TrendingUp className="w-3 h-3" />
                            {category.job_count} active jobs
                          </div>
                        )}
                      </div>
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                        isSelected ? 'bg-primary-600' : 'bg-gray-200 group-hover:bg-gray-300'
                      }`}>
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            
            {showErrors && selectedCategories.length === 0 && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                <p className="text-sm text-red-700">Please select at least one service category</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Step 2: Service Areas */}
      <div className="space-y-4 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <MapPin className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Service Areas</h3>
              <p className="text-xs text-gray-500">Counties where you operate</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {selectedCounties.length > 0 && (
              <Badge variant="success" size="sm">
                {selectedCounties.length} counties
              </Badge>
            )}
            <button
              type="button"
              onClick={selectAllCounties}
              className="text-xs font-medium text-blue-600 hover:text-blue-700 px-2 py-1 rounded hover:bg-blue-50"
            >
              All
            </button>
            <button
              type="button"
              onClick={clearAllCounties}
              className="text-xs font-medium text-gray-600 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100"
            >
              Clear
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-3 text-sm text-gray-500">Loading counties...</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 max-h-80 overflow-y-auto">
              {Object.entries(countiesByProvince).map(([province, provinceCounties]) => (
                <div key={province} className="mb-3 last:mb-0">
                  <button
                    type="button"
                    className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-white transition-colors border border-transparent hover:border-gray-200"
                    onClick={() => toggleProvince(province)}
                  >
                    {isProvinceFullySelected(province) ? (
                      <CheckSquare className="w-5 h-5 text-primary-600 flex-shrink-0" />
                    ) : isProvincePartiallySelected(province) ? (
                      <div className="w-5 h-5 border-2 border-primary-600 rounded flex items-center justify-center flex-shrink-0">
                        <div className="w-2.5 h-2.5 bg-primary-600 rounded-sm" />
                      </div>
                    ) : (
                      <Square className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    )}
                    <span className="font-semibold text-gray-900 flex-1 text-left">{province}</span>
                    <Badge variant="secondary" size="sm">
                      {provinceCounties.filter(c => selectedCounties.includes(c.id)).length}/{provinceCounties.length}
                    </Badge>
                  </button>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 ml-8 mt-2">
                    {provinceCounties.map((county) => (
                      <label
                        key={county.id}
                        className={`flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg transition-all ${
                          selectedCounties.includes(county.id)
                            ? 'bg-primary-50 border border-primary-200'
                            : 'hover:bg-white border border-transparent'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedCounties.includes(county.id)}
                          onChange={() => toggleCounty(county.id)}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className={`text-sm ${
                          selectedCounties.includes(county.id) ? 'font-medium text-gray-900' : 'text-gray-700'
                        }`}>
                          {county.name}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {showErrors && selectedCounties.length === 0 && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                <p className="text-sm text-red-700">Please select at least one service area</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Step 3: Pricing & Availability */}
      <div className="space-y-4 pt-4 border-t border-gray-200">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
            <DollarSign className="w-4 h-4 text-emerald-600" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">Pricing & Availability</h3>
            <p className="text-xs text-gray-500">Optional - can be updated later</p>
          </div>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 space-y-5">
          {/* Pricing Grid */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Callout Fee (€)
              </label>
              <input
                type="number"
                value={data.calloutFee || ''}
                onChange={(e) => onChange({ ...data, calloutFee: e.target.value ? parseFloat(e.target.value) : undefined })}
                placeholder="0.00"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                min="0"
                step="0.01"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum Job Value (€)
              </label>
              <input
                type="number"
                value={data.minimumJobValue || ''}
                onChange={(e) => onChange({ ...data, minimumJobValue: e.target.value ? parseFloat(e.target.value) : undefined })}
                placeholder="0.00"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                min="0"
                step="0.01"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Travel Distance (km)
              </label>
              <input
                type="number"
                value={data.travelDistance || ''}
                onChange={(e) => onChange({ ...data, travelDistance: e.target.value ? parseInt(e.target.value) : undefined })}
                placeholder="25"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Travel Fee (€)
              </label>
              <input
                type="number"
                value={data.travelFee || ''}
                onChange={(e) => onChange({ ...data, travelFee: e.target.value ? parseFloat(e.target.value) : undefined })}
                placeholder="0.00"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                min="0"
                step="0.01"
              />
            </div>
          </div>

          {/* Availability Checkboxes */}
          <div className="pt-3 border-t border-gray-300">
            <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Availability Options
            </label>
            <div className="space-y-3">
              <label className="flex items-center gap-3 p-3 rounded-lg hover:bg-white cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={data.emergencyService || false}
                  onChange={(e) => onChange({ ...data, emergencyService: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900">Emergency Services</span>
                  <p className="text-xs text-gray-500">Available for urgent/emergency calls</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 rounded-lg hover:bg-white cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={data.weekendService || false}
                  onChange={(e) => onChange({ ...data, weekendService: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900">Weekend Services</span>
                  <p className="text-xs text-gray-500">Available on Saturdays and Sundays</p>
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
